import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ENTITY_TYPES,
  TIME_INPUT_MODES,
  normalizeEntityType,
  type CustomQuestion,
  type Entity,
  type EntityType,
  type EventOption,
  type TimeInputMode,
} from "../../types";
import { EventOptionsEditor } from "./EventOptionsEditor";
import { newOptionId } from "../../utils/newOptionId";
import { TIME_INPUT_HINTS, TIME_INPUT_LABELS, typeEventOptions, typeTimeInputMode } from "../../config/typeSettings";
import { useTypeSettings } from "../../contexts/TypeSettingsContext";
import { EntityFeatureToggles } from "./EntityFeatureToggles";
import { toggleFeature } from "../../utils/entityFeatures";
import { useAuth } from "../../contexts/AuthContext";
import { imageUploadErrorMessage, uploadEntityImage } from "../../utils/imageUpload";
import { EntityPicker } from "./EntityPicker";
import { entityTypeLabel } from "../../utils/tripDisplay";
import { WikiLinkControl } from "./WikiLinkControl";
import { CustomQuestionEditor } from "./CustomQuestionEditor";
import { getCustomQuestions, normalizeCustomQuestions } from "../../utils/customQuestions";

interface EntityFormData {
  displayName: string;
  type: EntityType;
  parentId: string | null;
  imageUrl: string;
  externalId: string;
  externalSource: string;
  activeFrom: string;
  activeTo: string;
  /** Null follows the type's default. */
  timeInputMode: TimeInputMode | null;
  eventOptions: EventOption[];
  customQuestions: CustomQuestion[];
}

interface EntityFormProps {
  initial?: Partial<Entity & { id: string }>;
  onSubmit: (data: EntityFormData) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
}

const PARENT_TYPES: EntityType[] = ["DESTINATION", "PARK", "EVENT", "HOTEL", "SHIP", "AIRPORT", "LOCATION"];

function timestampToDateInput(ts: { toDate: () => Date } | null | undefined): string {
  if (!ts) return "";
  const d = ts.toDate();
  return d.toISOString().substring(0, 10);
}

function typeLabel(type: string): string {
  return entityTypeLabel(type);
}

export function EntityForm({ initial, onSubmit, onCancel, submitLabel = "Create Entity" }: EntityFormProps) {
  const [displayName, setDisplayName] = useState(initial?.displayName || "");
  const [type, setType] = useState<EntityType>(normalizeEntityType(initial?.type) ?? "ATTRACTION");
  const [parentId, setParentId] = useState<string | null>(initial?.parentId || null);
  const [imageUrl, setImageUrl] = useState(initial?.imageUrl || "");
  const [externalId, setExternalId] = useState(initial?.externalId || "");
  const [externalSource, setExternalSource] = useState(initial?.externalSource || "");
  const [activeFrom, setActiveFrom] = useState(timestampToDateInput(initial?.activeFrom));
  const [activeTo, setActiveTo] = useState(timestampToDateInput(initial?.activeTo));
  // "" follows the type's default (see `config/typeSettings`).
  const [timeInputMode, setTimeInputMode] = useState<TimeInputMode | "">(() => {
    const own = initial?.customConfig?.time_input_mode;
    return (TIME_INPUT_MODES as readonly unknown[]).includes(own) ? (own as TimeInputMode) : "";
  });
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>(() =>
    getCustomQuestions(initial as Pick<Entity, "customConfig"> | undefined),
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, userProfile } = useAuth();
  const canManage = !!userProfile?.roles?.includes("manage_entities");
  const typeSettings = useTypeSettings(type);
  // The type's steps as they stand — saved in the app, else built in.
  const typeSteps = typeEventOptions(type, typeSettings);
  const typeMode = typeTimeInputMode(type, typeSettings);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file || !user) return;
    setError(null);
    setUploading(true);
    try {
      setImageUrl(await uploadEntityImage(file, user.uid));
    } catch (err) {
      console.error("Image upload failed", err);
      setError(imageUploadErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const activityOptions = initial?.activityOptions ?? [];
  const [eventOptions, setEventOptions] = useState<EventOption[]>(initial?.eventOptions ?? []);

  // Features are the way in for almost every entity; the step list below is
  // for the exceptions. See `config/entityFeatures`.
  const handleFeature = (featureId: string, on: boolean) =>
    setEventOptions((prev) => toggleFeature(prev, type, featureId, on, newOptionId));


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!displayName.trim()) {
      setError("Display name is required.");
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        displayName: displayName.trim(),
        type,
        parentId,
        imageUrl: imageUrl.trim(),
        externalId: externalId.trim(),
        externalSource: externalSource.trim(),
        activeFrom,
        activeTo,
        timeInputMode: timeInputMode || null,
        eventOptions: eventOptions
          .filter((o) => o.displayName.trim())
          .map((o) => ({ ...o, displayName: o.displayName.trim() })),
        customQuestions: normalizeCustomQuestions(customQuestions),
      });
    } catch {
      setError("Failed to save entity. Please try again.");
      setSaving(false);
    }
  };

  return (
    <form className="form" onSubmit={handleSubmit}>
      {error && <div className="form-error">{error}</div>}

      <label className="form-field">
        <span className="form-label">Display Name</span>
        <input
          type="text"
          className="form-input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. Space Mountain, Magic Kingdom"
          autoFocus
        />
      </label>

      <label className="form-field">
        <span className="form-label">Type</span>
        <select
          className="form-input"
          value={type}
          onChange={(e) => setType(e.target.value as EntityType)}
        >
          {ENTITY_TYPES.map((t) => (
            <option key={t} value={t}>{typeLabel(t)}</option>
          ))}
        </select>
      </label>

      {/* Parent entity picker */}
      <div className="form-field">
        <span className="form-label">Parent Entity (optional)</span>
        <EntityPicker
          types={PARENT_TYPES}
          value={parentId}
          onChange={(id) => setParentId(id)}
          placeholder="Select parent..."
        />
      </div>

      <div className="form-field">
        <span className="form-label">Image (optional)</span>
        {imageUrl && (
          <div className="entity-image-preview">
            <img src={imageUrl} alt="" />
            <button type="button" className="btn-link" onClick={() => setImageUrl("")}>
              Remove
            </button>
          </div>
        )}
        <label className="btn btn-secondary btn-small entity-upload-btn">
          {uploading ? "Uploading…" : "📷 Upload / take photo"}
          <input
            type="file"
            accept="image/*"
            hidden
            disabled={uploading || saving}
            onChange={handleFile}
          />
        </label>
        <input
          type="url"
          className="form-input"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          placeholder="…or paste an image URL"
        />
      </div>

      {/* ThemeParks.wiki linking — browse/pick a wiki entity (and bulk-create children). */}
      <WikiLinkControl
        externalSource={externalSource}
        externalId={externalId}
        onChange={(src, id) => { setExternalSource(src); setExternalId(id); }}
        entityType={type}
        entityId={initial?.id}
      />

      <div className="form-row">
        <label className="form-field">
          <span className="form-label">Active From</span>
          <input
            type="date"
            className="form-input"
            value={activeFrom}
            onChange={(e) => setActiveFrom(e.target.value)}
          />
        </label>
        <label className="form-field">
          <span className="form-label">Active To</span>
          <input
            type="date"
            className="form-input"
            value={activeTo}
            onChange={(e) => setActiveTo(e.target.value)}
          />
        </label>
      </div>

      <label className="form-field">
        <span className="form-label">Time Input Mode</span>
        <select
          className="form-input"
          value={timeInputMode}
          onChange={(e) => setTimeInputMode(e.target.value as TimeInputMode | "")}
        >
          <option value="">{typeLabel(type)} default ({TIME_INPUT_LABELS[typeMode]})</option>
          {TIME_INPUT_MODES.map((m) => (
            <option key={m} value={m}>{TIME_INPUT_LABELS[m]}</option>
          ))}
        </select>
        <span className="form-hint">{TIME_INPUT_HINTS[timeInputMode || typeMode]}</span>
      </label>

      <CustomQuestionEditor questions={customQuestions} onChange={setCustomQuestions} />
      {customQuestions.length === 0 && (
        <span className="form-hint">With none of its own, a place asks its type's questions.</span>
      )}
      {canManage && (
        <Link to={`/types/${type}`} className="btn-link type-defaults-link">
          Edit the {typeLabel(type)} defaults →
        </Link>
      )}

      <div className="form-field">
        <EntityFeatureToggles type={type} options={eventOptions} onToggle={handleFeature} />
      </div>

      {/* Log-event editor — the one-tap steps shown when logging here. */}
      <EventOptionsEditor
        options={eventOptions}
        setOptions={setEventOptions}
        flowDefaults={typeSteps}
        defaultsToLoad={typeSteps}
        typeName={typeLabel(type)}
        entityType={type}
        hint={
          <>
            The one-tap steps shown when logging an activity here, in the order they happen. Each
            step shows the buttons offered after it; tap a step to rename it, change its kind, or
            put its buttons in order.
          </>
        }
      />

      {activityOptions.length > 0 && (
        <div className="form-field">
          <span className="form-label">Access Tiers</span>
          <span className="form-hint">From imported data; not editable here yet.</span>
          <ul className="tag-list">
            {activityOptions.map((opt) => (
              <li key={opt.id} className="tag">
                {opt.displayName}
                <span className="text-muted"> ({opt.optionType})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="form-actions">
        <button type="submit" className="btn" disabled={saving || uploading}>
          {saving ? "Saving..." : submitLabel}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}
